import { API_BASE } from './api-base';
import { clearPromo, loadPromo, promoToSpend, type StoredPromo } from './promo-code';
import { TERMINAL_PROMO_OUTCOMES, type PromoOutcome } from './promo-outcome';

export interface PromoRedemption {
  outcome: PromoOutcome;
  /** Credits granted by THIS redemption. Zero on every outcome but `redeemed`. */
  credits: number;
  /** Free-usage hours granted by THIS redemption. Zero on every outcome but `redeemed`. */
  freeHours: number;
  /** The `?source=` tag the code was spent with, for BI. */
  source: string;
}

/** One redemption attempt. Never throws — a promo must not be able to break the page it lands on. */
export async function redeemPromo(code: string, source: string): Promise<PromoRedemption> {
  try {
    const res = await fetch(`${API_BASE}/api/promo-redeem`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, ...(source ? { source } : {}) }),
    });
    if (!res.ok) return { outcome: 'retry', credits: 0, freeHours: 0, source };

    const body = (await res.json()) as { outcome?: PromoOutcome; credits?: number; freeHours?: number };
    return { outcome: body.outcome ?? 'failed', credits: body.credits ?? 0, freeHours: body.freeHours ?? 0, source };
  } catch {
    // Offline, blocked, or a malformed answer — keep the code and try again next time.
    return { outcome: 'retry', credits: 0, freeHours: 0, source };
  }
}

/**
 * Spend the code held at signup, now that the caller has an account that can receive credits.
 *
 * Called from the middle of the phone-verification success path, beside `claimStoredReferral` and
 * for the same reason: that is the first moment a brand-new user has a metasite. Anything that
 * escaped here would surface as a failed verification — of a verification that already succeeded —
 * so this never throws.
 *
 * The code is dropped only on a TERMINAL outcome. Anything retryable is kept, so a grant is not
 * lost to the recipient's read-after-write lag.
 */
export async function redeemStoredPromo(userId: string): Promise<PromoRedemption | null> {
  try {
    const stored: StoredPromo | null = loadPromo();
    const spendable = promoToSpend({ stored, userId, now: Date.now() });
    if (!spendable) {
      // Expired, absent, or captured for a different signed-in user. An unusable record is dropped
      // so it cannot linger in a shared browser.
      if (stored) clearPromo();
      return null;
    }

    const redemption = await redeemPromo(spendable.code, spendable.source);
    if (TERMINAL_PROMO_OUTCOMES.has(redemption.outcome)) clearPromo();
    return redemption;
  } catch {
    return null;
  }
}
